import React, { useEffect, useState } from "react";
import DashboardShell from "../../components/DashboardShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download, FileLock2, Plus, ScrollText, X } from "lucide-react";

export default function AuditBundles() {
  const [bundles, setBundles] = useState([]);
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const load = () => api.get("/v1/audit-bundles").then(r => setBundles(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    await api.post("/v1/audit-bundles");
    toast.success("Signed bundle created");
    load();
  };

  const openKit = async (b) => {
    setKitLoading(true);
    try {
      const { data } = await api.get(`/v1/regulator-kit/${b.id}`);
      setKit(data);
    } catch { toast.error("Failed to generate kit"); }
    finally { setKitLoading(false); }
  };

  const downloadKit = () => {
    if (!kit) return;
    const blob = new Blob([JSON.stringify(kit, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `regulator-kit-${kit.bundle.id}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Kit downloaded");
  };

  return (
    <DashboardShell
      title="Audit Bundles"
      subtitle="Signed, reproducible snapshots for regulator response."
      actions={<button data-testid="new-bundle" onClick={create} className="btn-primary text-sm"><Plus size={14}/> New bundle</button>}
    >
      <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-cream/60">
            <tr className="text-left">
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>Bundle</th>
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>Decisions</th>
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>Size</th>
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>SHA-256</th>
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>Created</th>
              <th className="px-4 py-3 eyebrow" style={{fontSize:10}}>Kit</th>
            </tr>
          </thead>
          <tbody>
            {bundles.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-cream/50">No bundles yet.</td></tr>}
            {bundles.map(b => (
              <tr key={b.id} data-testid={`bundle-${b.id}`} className="border-t border-white/5">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileLock2 size={14} className="text-[#FF6B35]"/>
                    <span className="font-mono text-xs">{b.id}</span>
                  </div>
                </td>
                <td className="px-4 py-3">{b.decision_count}</td>
                <td className="px-4 py-3">{b.size_kb} KB</td>
                <td className="px-4 py-3 font-mono text-[11px] text-cream/60 max-w-[220px] truncate">{b.sha256}</td>
                <td className="px-4 py-3 text-cream/60 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <button data-testid={`regulator-kit-${b.id}`} onClick={() => openKit(b)} className="btn-primary text-xs">
                    <ScrollText size={12}/> Regulator Kit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(kitLoading || kit) && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !kitLoading && setKit(null)}>
          <div className="bg-[#0A0E1A] border border-white/15 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
              <ScrollText size={18} className="text-[#FF6B35]"/>
              <div>
                <div className="font-display text-lg font-bold">Regulator Response Kit</div>
                <div className="text-xs text-cream/50">EU AI Act Article 14 · SOC 2 · HIPAA · FedRAMP mapping</div>
              </div>
              <button onClick={() => setKit(null)} className="ml-auto p-2 rounded hover:bg-white/10"><X size={16}/></button>
            </div>
            {kitLoading && <div className="p-8 text-center text-cream/60">Generating signed kit…</div>}
            {kit && (
              <div className="overflow-y-auto scroll-dark p-6 space-y-5">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg bg-white/5 p-3"><div className="eyebrow text-[#FFD98A]" style={{fontSize:10}}>KIT ID</div><div className="font-mono text-xs mt-1 break-all">{kit.kit_id}</div></div>
                  <div className="rounded-lg bg-white/5 p-3"><div className="eyebrow text-[#FFD98A]" style={{fontSize:10}}>TENANT</div><div className="mt-1">{kit.tenant_name}</div></div>
                  <div className="rounded-lg bg-white/5 p-3"><div className="eyebrow text-[#FFD98A]" style={{fontSize:10}}>GENERATED</div><div className="mt-1 text-xs">{new Date(kit.generated_at).toLocaleString()}</div></div>
                </div>

                <div className="rounded-lg bg-white/5 p-4">
                  <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>CHAIN-OF-CUSTODY SHA-256</div>
                  <div className="font-mono text-[11px] break-all text-cream/80">{kit.chain_of_custody_sha256}</div>
                </div>

                <div>
                  <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>DECISION COUNTS</div>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(kit.decision_counts).map(([k, v]) => (
                      <div key={k} className="rounded-lg bg-white/5 p-3 text-center">
                        <div className="text-2xl font-display font-bold">{v}</div>
                        <div className="text-[10px] uppercase tracking-widest text-cream/50">{k}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>POLICY VERSIONS ({kit.policies.length})</div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto scroll-dark">
                    {kit.policies.map(p => (
                      <div key={p.id} className="flex items-center gap-3 text-xs bg-white/5 rounded p-2">
                        <span className="chip chip-cream text-[10px]">{p.action}</span>
                        <span className="font-semibold">{p.name}</span>
                        <span className="text-cream/50 ml-auto">v{p.version}</span>
                        <span className="font-mono text-cream/40 text-[10px]">{p.sha256.slice(0, 12)}…</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>EU AI ACT · ARTICLE 14 MAPPING</div>
                  <div className="space-y-2">
                    {kit.article_14_mapping.map((m, i) => (
                      <div key={i} className="rounded-lg bg-white/5 p-3 text-sm">
                        <div className="font-semibold text-[#FFD98A]">{m.clause}</div>
                        <div className="text-cream/75 mt-1">{m.control}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {kit.frameworks.map(f => <span key={f} className="chip chip-cream text-[10px]">{f}</span>)}
                </div>
              </div>
            )}
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
              <button onClick={() => setKit(null)} className="btn-ghost-dark text-xs">Close</button>
              <button data-testid="kit-download" onClick={downloadKit} disabled={!kit} className="btn-primary text-xs"><Download size={12}/> Download signed kit</button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
